import { useEffect, useState } from 'react';

import { useStore } from 'react-redux';
import styled from 'styled-components';

import Button from 'src/app/components/Elements/Button';
import PlusMinusButton from 'src/app/components/Elements/Button/PlusMinusButton';
import { cartAddItems, cartRemoveItems, cartUpdateItem } from 'src/app/state/actions';
import { getCartItems } from 'src/app/state/selectors';
import { useDispatch } from 'src/app/state/store';

const Container = styled.div`
  display: flex; /* Enable flexbox */
  justify-content: space-between; /* Push buttons to opposite sides */
  align-items: center; /* Align buttons vertically in the middle */
  flex: 0;
  padding-bottom: 0;
`;

const QuantityPicker = styled(PlusMinusButton)``;

const AddButton = styled(Button)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0;
`;

export interface CartControlsProps {
  id: string;
  price: number;
  name: string;
  minQuantity: number;
  maxQuantity: number;
  onAddToCart?: () => void;
  onRemoveFromCart?: () => void;
}

export const CartControls = ({
  id,
  price,
  name,
  minQuantity,
  maxQuantity,
  onAddToCart,
  onRemoveFromCart,
}: CartControlsProps) => {
  const dispatch = useDispatch();
  const store = useStore();
  const state = store.getState();

  const [isInCart, setIsInCart] = useState(false);

  const otcCartItem = getCartItems(state).filter(item => item.otcItemId === id)[0];
  let quantity = otcCartItem?.quantity;
  const minQuantityWithDefault = minQuantity ?? 1;
  if (!quantity || quantity <= 0) quantity = minQuantityWithDefault;

  useEffect(() => {
    const minQuantityWithDefault = minQuantity ?? 1;
    if (otcCartItem?.quantity && otcCartItem.quantity >= minQuantityWithDefault) {
      setIsInCart(true);
    }
  }, [minQuantity, otcCartItem]);

  const [totalPrice, setTotalPrice] = useState(price * quantity);
  const [buttonQuantity, setButtonQuantity] = useState(quantity);

  const dispatchAddToCart = (id: string, price: number, quantity: number) => {
    if (onAddToCart) {
      onAddToCart();
    }

    dispatch(
      cartAddItems({
        otcItemId: id,
        price,
        quantity,
      })
    );
  };
  const dispatchRemoveFromCart = (id: string, price: number, quantity: number) => {
    if (onRemoveFromCart) {
      onRemoveFromCart();
    }

    dispatch(
      cartRemoveItems({
        otcItemId: id,
        price,
        quantity,
      })
    );
  };

  const dispatchUpdateCart = (id: string, price: number, quantity: number) => {
    dispatch(
      cartUpdateItem({
        otcItemId: id,
        price,
        quantity,
      })
    );
  };

  return (
    <Container>
      <QuantityPicker
        onChange={newQuantity => {
          dispatchUpdateCart(id, newQuantity * price, newQuantity);
          setButtonQuantity(newQuantity);
          setTotalPrice(newQuantity * price);
        }}
        initialQuantity={quantity}
        minQuantity={minQuantityWithDefault}
        maxQuantity={maxQuantity}
        otcName={name}
        disabled={false}
        onDecrease={() => {
          if (isInCart && onRemoveFromCart) {
            onRemoveFromCart();
          }
        }}
        onIncrease={() => {
          if (isInCart && onAddToCart) {
            onAddToCart();
          }
        }}
      />

      {isInCart ? (
        <AddButton
          style="secondary"
          size="small"
          onClick={() => {
            dispatchRemoveFromCart(id, totalPrice, buttonQuantity);
            setIsInCart(false);
          }}
          data-cy={`in-bag-${name.split(' ').join('-')}`}
          aria-label={`Remove ${name} from bag`}
        >
          Remove
        </AddButton>
      ) : (
        <AddButton
          style="primary"
          size="small"
          onClick={() => {
            dispatchAddToCart(id, totalPrice, buttonQuantity);
            setIsInCart(true);
          }}
          data-cy={`add-to-bag-${name.split(' ').join('-')}`}
          aria-label={`Add ${name} to bag`}
        >
          Add To Bag
        </AddButton>
      )}
    </Container>
  );
};
